// Teacher invitation (docs/project.md §7; Invite board): preview → sign in → accept → the
// student's dictionary opens; onboarding is skipped for an invited teacher (§4).
import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useLocale, useTranslations } from 'use-intl';
import { languageName, languageTitle } from '@solungo/core/lang';
import { api } from '@/api/endpoints';
import { ApiRequestError } from '@/api/client';
import { loadUser, refreshDictionaries } from '@/state/session';
import { Person } from '@/components/Icons';
import { ErrorText } from '@/components/ui';
import { AuthFrame } from '@/components/AuthFrame';
import { reportError } from '@/lib/errors';
import { track } from '@/lib/analytics';

export const Route = createFileRoute('/share/$token')({
  loader: async ({ params }) => {
    const [user, share] = await Promise.all([
      loadUser().catch((e: unknown) => {
        reportError(e);
        return null;
      }),
      api.shares.lookup(params.token).catch((e: unknown) => {
        reportError(e);
        return { error: e instanceof ApiRequestError ? e.code : 'unknown' };
      }),
    ]);
    return { user, share };
  },
  component: ShareScreen,
});

function ShareScreen() {
  const t = useTranslations('share');
  const tc = useTranslations('common');
  const locale = useLocale();
  const { token } = Route.useParams();
  const { user, share } = Route.useLoaderData();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if ('error' in share) {
    return (
      <AuthFrame title={t('title')} subtitle={t('invalid')}>
        <button type="button" className="btn-secondary" onClick={() => void navigate({ to: '/' })}>
          {tc('close')}
        </button>
      </AuthFrame>
    );
  }

  const studentName = share.ownerName;
  const student = studentName ? t('student', { name: studentName }) : t('student', { name: '' }).trim();

  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      let dictionaryId = share.dictionary.id;
      if (share.status !== 'accepted') {
        const res = await api.shares.accept(token);
        track('share_accepted');
        dictionaryId = res.dictionary.id;
      }
      await refreshDictionaries();
      void navigate({ to: '/d/$dictionaryId', params: { dictionaryId }, replace: true });
    } catch (e) {
      reportError(e);
      setError(e instanceof ApiRequestError && e.code === 'share_invalid' ? t('invalid') : tc('errorGeneric'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame title={t('title')} subtitle={studentName ? t('text', { name: studentName, gender: 'other' }) : t('textNoName')}>
      <div className="flex items-center gap-3.5 rounded-card bg-surface p-4 desktop:gap-3 desktop:rounded-xl desktop:bg-canvas desktop:px-3.5 desktop:py-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#E8E3F7] text-teacher desktop:h-10 desktop:w-10">
          <Person />
        </span>
        <span className="flex min-w-0 flex-grow flex-col gap-0.5 desktop:gap-px">
          <span className="text-[17px] font-semibold leading-[22px] desktop:text-[15px] desktop:leading-5">{languageTitle(share.dictionary.targetLang, locale)}</span>
          <span className="text-[13px] leading-[17px] text-ink-secondary desktop:text-[12px] desktop:leading-4">
            {student} · {t('via', { language: languageName(share.dictionary.baseLang, locale) })}
          </span>
        </span>
      </div>
      {share.status === 'revoked' ? (
        <p className="m-0 text-[15px] text-ink-secondary desktop:text-[13px]">{t('revoked')}</p>
      ) : (
        <div className="flex flex-col gap-3 desktop:gap-2">
          {/* Phone: accept above decline; desktop: decline left, accept right (Invite boards) */}
          <div className="flex flex-col gap-3 desktop:flex-row-reverse desktop:gap-2">
            {user ? (
              <button type="button" className="btn-primary desktop:min-w-0 desktop:flex-1" disabled={busy} data-testid="share-accept" onClick={() => void accept()}>
                {share.status === 'accepted' ? tc('close') : t('accept')}
              </button>
            ) : (
              <button type="button" className="btn-primary desktop:min-w-0 desktop:flex-1" onClick={() => void navigate({ to: '/auth', search: { share: token } })}>
                {t('signInToAccept')}
              </button>
            )}
            <button type="button" className="btn-secondary desktop:min-w-0 desktop:flex-1" onClick={() => void navigate({ to: '/' })}>
              {t('decline')}
            </button>
          </div>
          <ErrorText>{error}</ErrorText>
        </div>
      )}
      <p className="m-0 text-[13px] leading-[18px] text-ink-secondary desktop:text-[12px] desktop:leading-[17px]">{t('footnote', { student })}</p>
    </AuthFrame>
  );
}
